import { useState, useMemo } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthorization } from "@/hooks/useAuthorization";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  COLLEGE_STATUS,
  type CollegeStatus,
  getCollegeStatusLabel,
} from "@/types/enums";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Search,
  Edit,
  Calendar,
  MapPin,
  Users,
  Trash2,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import {
  CollegeDialog,
  CollegeFormData,
} from "@/components/colleges/CollegeDialog";
import { CollegeAssessmentActions } from "@/components/colleges/CollegeAssessmentActions";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import type { Tables } from "@/integrations/supabase/types";
import { useSearchParams } from "react-router-dom";
import { getSafeErrorMessage } from "@/utils/safeErrorMessage";

type College = Tables<"colleges"> & {
  candidates?: { id: string; created_at: string | null }[];
  cv_count?: number;
  status?: string;
  joining_date?: string | null;
};

const FILTER_PARAM_KEYS = {
  search: "search",
  city: "city",
  status: "status",
  year: "year",
  visitDateSort: "visitDateSort",
} as const;

export default function CollegesPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCollege, setEditingCollege] = useState<College | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [collegeToDelete, setCollegeToDelete] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentYear = new Date().getFullYear();
  const currentYearStr = currentYear.toString();

  const searchTerm = searchParams.get(FILTER_PARAM_KEYS.search) ?? "";
  const cityFilter = searchParams.get(FILTER_PARAM_KEYS.city) ?? "all";
  const statusFilter = searchParams.get(FILTER_PARAM_KEYS.status) ?? "all";
  const yearFilter = searchParams.get(FILTER_PARAM_KEYS.year) ?? currentYearStr;
  const rawVisitDateSort =
    searchParams.get(FILTER_PARAM_KEYS.visitDateSort) ?? "none";
  const visitDateSort: "asc" | "desc" | "none" =
    rawVisitDateSort === "asc" || rawVisitDateSort === "desc"
      ? rawVisitDateSort
      : "none";

  const updateFilterParam = (
    key: string,
    value: string,
    options?: { emptyValue?: string },
  ) => {
    const emptyValue = options?.emptyValue ?? "all";

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);

      if (!value || value === emptyValue) {
        next.delete(key);
      } else {
        next.set(key, value);
      }

      return next;
    });
  };

  const setSearchTerm = (value: string) =>
    updateFilterParam(FILTER_PARAM_KEYS.search, value, { emptyValue: "" });
  const setCityFilter = (value: string) =>
    updateFilterParam(FILTER_PARAM_KEYS.city, value, { emptyValue: "all" });
  const setStatusFilter = (value: string) =>
    updateFilterParam(FILTER_PARAM_KEYS.status, value, { emptyValue: "all" });
  const setYearFilter = (value: string) =>
    updateFilterParam(FILTER_PARAM_KEYS.year, value, {
      emptyValue: currentYearStr,
    });
  const setVisitDateSort = (value: "asc" | "desc" | "none") =>
    updateFilterParam(FILTER_PARAM_KEYS.visitDateSort, value, {
      emptyValue: "none",
    });

  const getCandidatesPageUrl = (collegeId: string) => {
    const params = new URLSearchParams();
    params.set("college", collegeId);

    if (yearFilter !== currentYearStr) {
      params.set("year", yearFilter);
    }

    return `/admin/candidates?${params.toString()}`;
  };

  const { hasPermission, loading } = useAuthorization();
  const canRead = hasPermission('colleges.read');
  const canWrite = hasPermission('colleges.write');

  const {
    data: colleges = [],
    isLoading,
  } = useQuery({
    queryKey: ["colleges", yearFilter],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("colleges")
        .select(`*, candidates(id, created_at)`)
        .eq("is_deleted", false)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const parsedYear =
        yearFilter === "all" ? null : Number.parseInt(yearFilter, 10);
      const selectedYear =
        parsedYear !== null && Number.isNaN(parsedYear) ? null : parsedYear;

      return ((data || []) as College[]).map((college) => {
        const candidates = college.candidates || [];
        const cvCount =
          selectedYear === null
            ? candidates.length
            : candidates.filter((candidate) => {
                if (!candidate.created_at) return false;
                return new Date(candidate.created_at).getUTCFullYear() === selectedYear;
              }).length;

        return {
          ...college,
          cv_count: cvCount,
        };
      });
    },
    enabled: canRead,
  });

  const createMutation = useMutation({
    mutationFn: async (collegeData: CollegeFormData) => {
      const { data, error } = await supabase
        .from("colleges")
        .insert([collegeData])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["colleges"] });
      setIsDialogOpen(false);
      toast({
        title: "Success",
        description: "College created successfully.",
      });
    },
    onError: (error: unknown) => {
      console.error("Create college error:", error);

      toast({
        title: "Error",
        description: getSafeErrorMessage(error, "Failed to create college."),
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      ...collegeData
    }: Partial<CollegeFormData> & { id: string }) => {
      const { data, error } = await supabase
        .from("colleges")
        .update(collegeData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (updatedCollege) => {
      queryClient.setQueryData(["selected-college", updatedCollege.id], updatedCollege);
      queryClient.invalidateQueries({ queryKey: ["colleges"] });
      setIsDialogOpen(false);
      setEditingCollege(null);
      toast({
        title: "Success",
        description: "College updated successfully.",
      });
    },
    onError: (error) => {
      console.error("Update college error:", error);
      toast({
        title: "Error",
        description: "Failed to update college.",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("colleges")
        .update({ is_deleted: true })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["colleges"] });
      setDeleteConfirmOpen(false);
      setCollegeToDelete(null);
      toast({
        title: "Success",
        description: "College deleted successfully.",
      });
    },
    onError: (error) => {
      console.error("Delete college error:", error);
      setDeleteConfirmOpen(false);
      setCollegeToDelete(null);
      toast({
        title: "Error",
        description: "Failed to delete college.",
        variant: "destructive",
      });
    },
  });

  const toggleEnabledMutation = useMutation({
    mutationFn: async ({ id, is_enabled }: { id: string; is_enabled: boolean }) => {
      const { error } = await supabase
        .from("colleges")
        .update({ is_enabled })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["colleges"] });
      queryClient.invalidateQueries({ queryKey: ["candidates-enhanced"] });
      queryClient.invalidateQueries({ queryKey: ["colleges-filter"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-colleges"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-candidates"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-rounds"] });
      queryClient.invalidateQueries({ queryKey: ["colleges-public"] });
      queryClient.invalidateQueries({ queryKey: ["college-by-code"] });
      toast({
        title: "Success",
        description: variables.is_enabled
          ? "College enabled successfully."
          : "College disabled successfully.",
      });
    },
    onError: (error: unknown) => {
      console.error("Toggle college error:", error);
      toast({
        title: "Error",
        description: getSafeErrorMessage(error, "Failed to update college status."),
        variant: "destructive",
      });
    },
  });

  const uniqueCities = useMemo(
    () =>
      Array.from(
        new Set(
          colleges.map((c) => c.city).filter((c): c is string => Boolean(c))
        )
      ),
    [colleges]
  );

  const yearOptions = useMemo(
    () => Array.from({ length: 5 }, (_, i) => (currentYear - i).toString()),
    [currentYear]
  );

  const filteredColleges = colleges
    .filter(
      (college) =>
        college.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        college.college_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        college.city?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter((college) => cityFilter === "all" || college.city === cityFilter)
    .filter(
      (college) => statusFilter === "all" || college.status === statusFilter
    );

  const sortedColleges = [...filteredColleges].sort((a, b) => {
    if (visitDateSort === "none") return 0;
    const aDate = a.college_visit_date
      ? new Date(a.college_visit_date).getTime()
      : 0;
    const bDate = b.college_visit_date
      ? new Date(b.college_visit_date).getTime()
      : 0;
    return visitDateSort === "asc" ? aDate - bDate : bDate - aDate;
  });

  const handleCreate = (collegeData: CollegeFormData) => {
    createMutation.mutate(collegeData);
  };

  const handleUpdate = (collegeData: CollegeFormData) => {
    if (!editingCollege) return;
    updateMutation.mutate({ id: editingCollege.id, ...collegeData });
  };

  const handleEdit = (college: College) => {
    setEditingCollege(college);
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    setCollegeToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (collegeToDelete) {
      deleteMutation.mutate(collegeToDelete);
    }
  };


  if (loading || isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">Loading colleges...</div>
      </div>
    );
  }

  if (!canRead) {
    return <div className="p-4">You do not have permission to view this page.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Colleges</h1>
          <p className="text-muted-foreground mt-2">
            Manage college partnerships and drive details
          </p>
        </div>
        {canWrite && (
          <Button onClick={() => { setEditingCollege(null); setIsDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add College
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Colleges</CardTitle>
          <CardDescription>
            {filteredColleges.length} colleges found
          </CardDescription>

          <div className="flex flex-wrap items-end gap-4 mt-4">
            <div className="flex items-center space-x-2">
              <Search className="h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search colleges..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm"
              />
            </div>
            <div className="flex items-center space-x-2">
              <MapPin className="h-4 w-4 text-gray-400" />
              <select
                className="border rounded-md p-2 text-sm"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
              >
                <option value="all">All Cities</option>
                {uniqueCities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-gray-400 text-sm">Status</span>
              <select
                className="border rounded-md p-2 text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                {Object.values(COLLEGE_STATUS).map((s) => (
                  <option key={s} value={s}>
                    {getCollegeStatusLabel(s as CollegeStatus)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-gray-400 text-sm">Year</span>
              <select
                className="border rounded-md p-2 text-sm"
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
              >
                <option value="all">All Years</option>
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-gray-400" />
              <select
                className="border rounded-md p-2 text-sm"
                value={visitDateSort}
                onChange={(e) =>
                  setVisitDateSort(e.target.value as "asc" | "desc" | "none")
                }
              >
                <option value="none">Default Order</option>
                <option value="asc">Visit Date ↑</option>
                <option value="desc">Visit Date ↓</option>
              </select>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>City</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joining Date</TableHead>
                <TableHead>Registration Dates</TableHead>
                <TableHead>Visit Date</TableHead>
                <TableHead>Placement Coord.</TableHead>
                <TableHead>CVs</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedColleges.map((college) => (
                <TableRow key={college.id} className={!college.is_enabled ? "opacity-50" : undefined}>
                  <TableCell className="font-medium">{college.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{college.college_code}</Badge>
                  </TableCell>
                  <TableCell>{college.city || "N/A"}</TableCell>
                <TableCell>
                  <Badge variant="secondary">
                    {college.status
                      ? getCollegeStatusLabel(college.status as CollegeStatus)
                      : "-"}
                  </Badge>
                </TableCell>
                <TableCell>
                  {college.joining_date
                    ? format(new Date(college.joining_date), "MMM dd, yyyy")
                    : "-"}
                </TableCell>
                <TableCell>
                  {college.drive_start_date && college.drive_end_date ? (
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3" />
                        <span className="text-sm">
                          {format(new Date(college.drive_start_date), "MMM dd")}{" "}
                          -{" "}
                          {format(
                            new Date(college.drive_end_date),
                            "MMM dd, yyyy"
                          )}
                        </span>
                      </div>
                    ) : (
                      "Not scheduled"
                    )}
                  </TableCell>
                  <TableCell>
                    {college.college_visit_date
                      ? format(
                          new Date(college.college_visit_date),
                          "MMM dd, yyyy"
                        )
                      : "Not scheduled"}
                  </TableCell>
                  <TableCell>
                    {college.placement_coordinator_name ? (
                      <div className="text-sm">
                        {college.placement_coordinator_name}
                        {college.placement_coordinator_phone && (
                          <> ({college.placement_coordinator_phone})</>
                        )}
                      </div>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{college.cv_count ?? 0}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {canWrite && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(college)}
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <CollegeAssessmentActions college={college} />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              toggleEnabledMutation.mutate({
                                id: college.id,
                                is_enabled: !college.is_enabled,
                              })
                            }
                            disabled={toggleEnabledMutation.isPending}
                            title={college.is_enabled ? "Disable College" : "Enable College"}
                            className={college.is_enabled ? "text-green-600 hover:text-green-800" : "text-gray-400 hover:text-gray-600"}
                          >
                            {college.is_enabled ? (
                              <ToggleRight className="h-4 w-4" />
                            ) : (
                              <ToggleLeft className="h-4 w-4" />
                            )}
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          window.open(
                            getCandidatesPageUrl(college.id),
                            "_blank"
                          )
                        }
                        title="View Candidates"
                      >
                        <Users className="h-4 w-4" />
                      </Button>
                      {canWrite && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(college.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <CollegeDialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) setEditingCollege(null);
        }}
        college={editingCollege}
        onSubmit={editingCollege ? handleUpdate : handleCreate}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete College</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this college? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
